import { useEffect, useState } from 'react'
import { Loader2, CheckCircle2 } from 'lucide-react'
import api from '../../services/api.js'
import SignaturePad from '../common/SignaturePad.jsx'
import BankingDetailsForm from '../contracts/BankingDetailsForm.jsx'
import { useTranslation } from 'react-i18next'

/**
 * AgentFormCard — a real form rendered inside the agent chat.
 *
 * This is the safety boundary of the agentic flow. Signatures, bank account numbers and identity
 * details are typed by the person they belong to and posted straight to the endpoint from here.
 * The AI decides WHICH form is needed and explains why; it never supplies those values, never
 * sees them, and they never pass through the model or the propose/confirm path.
 *
 * Deliberately mounts the app's EXISTING controls (SignaturePad, BankingDetailsForm) rather than
 * recreating them, so the chat and the normal screens can't drift apart on how signing works.
 */
export default function AgentFormCard({ form: initialForm, onDone }) {
  // One card can hand over to the next without a round trip to the model: confirming your bank
  // details goes straight on to signing the contract those details are for, which is the order
  // the manual screen puts them in and the order the person is thinking in.
  const [next, setNext] = useState(null)
  const form = next || initialForm
  const kind = form?.kind
  const [done, setDone] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  if (done) {
    return (
      <div className="mt-2 rounded-xl border border-green-200 bg-green-50 p-3 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
        <p className="text-xs text-green-800">{done}</p>
      </div>
    )
  }

  const finish = (msg) => { setDone(msg); onDone?.(msg) }

  const shell = (title, children) => (
    <div className="mt-2 rounded-xl border border-indigo-200 bg-white p-3">
      <p className="text-[11px] font-semibold text-indigo-700 mb-2">{title}</p>
      {error && <p className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mb-2">{error}</p>}
      {children}
    </div>
  )

  if (kind === 'banking') {
    return shell(form.has_banking ? 'Check your bank details' : 'Your bank details', (
      <BankingCard form={form} onDone={finish} onVerified={setNext} />
    ))
  }

  if (kind === 'contract_sign') {
    return shell(`Sign contract ${form.contract_number || ''}`.trim(), (
      <SignContract form={form} submitting={submitting} setSubmitting={setSubmitting}
                    setError={setError} onSigned={finish} />
    ))
  }

  if (kind === 'kyc_identity') {
    return shell('Your identity details', (
      <KycIdentity submitting={submitting} setSubmitting={setSubmitting}
                   setError={setError} onSaved={finish} />
    ))
  }

  if (kind === 'invoice_sign') {
    return shell(`Invoice — ${form.milestone_title || 'your milestone'}`, (
      <InvoiceSign form={form} submitting={submitting} setSubmitting={setSubmitting}
                   setError={setError} onSubmitted={finish} />
    ))
  }

  return null
}


/* ── Bank details: check what's on file, or enter them ────────────────────────────────────── */

/**
 * Mirrors the contract screen. If details are already stored, show them and ask the person to
 * confirm — the account number on file is where their money goes, and an empty form implies we
 * lost it. Only an explicit "Update" opens the editor.
 */
function BankingCard({ form, onDone, onVerified }) {
  const [data, setData] = useState(null)
  const [editing, setEditing] = useState(!form.has_banking)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    // The same endpoint the contract screen reads, so the two can never show different figures.
    api.get('/users/me/banking')
      .then(r => { if (alive) { setData(r.data); if (!r.data?.has_banking) setEditing(true) } })
      .catch(() => { if (alive) setEditing(true) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  if (loading) {
    return <p className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your bank details…</p>
  }

  // Confirmed or freshly saved — either way the details are settled, so go on to the signature
  // pad when these belong to a contract. Entering them and then being left with nothing to do
  // would strand the person one step short of the thing they asked for.
  const proceed = (message) => {
    if (form.contract_id) {
      onVerified({ kind: 'contract_sign', contract_id: form.contract_id,
                   contract_number: form.contract_number, project_name: form.project_name })
    } else {
      onDone(message)
    }
  }

  if (editing) {
    // Details are saved against a contract, so without one there is nothing to attach them to.
    if (!form.contract_id) {
      return (
        <p className="text-xs text-gray-600">
          Bank details are entered on your contract, and you don&rsquo;t have one yet. They&rsquo;ll be
          requested when your first contract is issued.
        </p>
      )
    }
    return (
      <BankingDetailsForm
        contractId={form.contract_id}
        initial={data?.has_banking ? data : null}
        onSaved={() => proceed('Bank details saved.')}
        onCancel={() => (data?.has_banking ? setEditing(false) : onDone('No problem — left your bank details as they were.'))}
      />
    )
  }

  const row = (label, value) => value ? (
    <div><span className="font-medium">{label}:</span> {value}</div>
  ) : null

  return (
    <div className="space-y-2.5">
      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
        These are the exact details that go on your contract and every invoice — payment is sent
        to this account.
      </p>
      <div className="text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-2.5 space-y-0.5">
        {row('A/C name', data.account_holder_name)}
        {row('Bank', data.bank_name)}
        {row('A/C no', data.account_number)}
        {row('SWIFT/BIC', data.swift_code)}
        {row('Currency', data.currency)}
      </div>
      {data.issues?.length > 0 && (
        <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          {data.issues.join(' ')}
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => proceed('Bank details confirmed.')}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg py-2">
          These are correct
        </button>
        <button onClick={() => setEditing(true)}
                className="px-3 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50">
          Update
        </button>
      </div>
    </div>
  )
}


/* ── Invoice: sign and submit ─────────────────────────────────────────────────────────────── */

function InvoiceSign({ form, submitting, setSubmitting, setError, onSubmitted }) {
  const { t } = useTranslation('assistant')
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [sig, setSig] = useState(null)
  const [claim, setClaim] = useState('')

  useEffect(() => {
    let alive = true
    // The figures come from the invoice-detail endpoint, never from the AI and never recomputed
    // here — the milestone/bill arithmetic must live in exactly one place.
    api.get(`/deliverables/invoices/${form.milestone_id}/detail`)
      .then(r => { if (alive) setDetail(r.data) })
      .catch(e => { if (alive) setError(e.response?.data?.detail || 'Could not load your invoice.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [form.milestone_id])

  if (loading) {
    return <p className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your invoice…</p>
  }
  if (!detail) return null

  const items = detail.line_items || []
  // A "fee" line is the server's own signal that this milestone is billed as a fixed fee. Using it
  // avoids keeping a third copy of the reimbursable rule in sync with the backend and the tab.
  const hasFeeLine = items.some(li => li.type === 'fee')
  const reimbTotal = detail.reimbursement_total_usd || 0
  const needsClaim = !hasFeeLine && reimbTotal <= 0

  const submit = async () => {
    if (!name.trim()) { setError('Please type your full name to sign.'); return }
    const body = { signature_name: name.trim(), signature_data: sig }
    if (needsClaim) {
      const amount = parseFloat(claim)
      if (isNaN(amount) || amount < 0) { setError('Please enter the amount you are claiming.'); return }
      body.claimed_amount = amount
    }
    setSubmitting(true); setError(null)
    try {
      await api.post(`/deliverables/invoices/confirm/${form.milestone_id}`, body)
      onSubmitted(`Invoice submitted for ${detail.milestone_title || 'your milestone'}. It's now with your approvers.`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit the invoice.')
    } finally { setSubmitting(false) }
  }

  return (
    <div className="space-y-2.5">
      <div className="text-[11px] text-gray-600">
        {form.project_name && <p className="mb-1">{t('project_label')} <span className="font-medium text-gray-800">{form.project_name}</span></p>}
        <div className="rounded-lg border border-gray-100 divide-y divide-gray-50">
          {items.map((li, i) => (
            <div key={i} className="flex justify-between gap-2 px-2 py-1.5">
              <span className="truncate">{li.label}</span>
              <span className="tabular-nums shrink-0">${Number(li.amount_usd || 0).toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between gap-2 px-2 py-1.5 font-semibold text-gray-900">
            <span>{t('total')}</span>
            <span className="tabular-nums">${Number(detail.total_usd || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {needsClaim && (
        <label className="block">
          <span className="text-[11px] font-medium text-gray-600 block mb-1">{t('amount_claiming')}</span>
          <input type="number" step="0.01" min="0" value={claim} onChange={e => setClaim(e.target.value)}
                 className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </label>
      )}

      <SignaturePad name={name} onNameChange={setName} signatureDataUrl={sig} onSignatureChange={setSig} />

      <button onClick={submit} disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg py-2">
        {submitting ? 'Submitting…' : 'Sign & submit invoice'}
      </button>
      <p className="text-[10px] text-gray-400">{t('typed_is_signature')}</p>
    </div>
  )
}


/* ── Contract: sign ───────────────────────────────────────────────────────────────────────── */

function SignContract({ form, submitting, setSubmitting, setError, onSigned }) {
  const { t } = useTranslation('assistant')
  const [name, setName] = useState('')
  const [sig, setSig] = useState(null)

  const submit = async () => {
    if (!name.trim()) { setError('Please type your full name to sign.'); return }
    setSubmitting(true); setError(null)
    try {
      await api.post(`/contracts/${form.contract_id}/sign/consultant`,
                     { signature_name: name.trim(), agreed: true, signature_data: sig })
      onSigned('Contract signed. It now goes for counter-signature.')
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not sign the contract.')
    } finally { setSubmitting(false) }
  }

  return (
    <div className="space-y-2.5">
      {form.project_name && <p className="text-[11px] text-gray-600">{t('project_label')} <span className="font-medium text-gray-800">{form.project_name}</span></p>}
      <SignaturePad name={name} onNameChange={setName} signatureDataUrl={sig} onSignatureChange={setSig} />
      <button onClick={submit} disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg py-2">
        {submitting ? 'Signing…' : 'Sign contract'}
      </button>
    </div>
  )
}


/* ── KYC identity ─────────────────────────────────────────────────────────────────────────── */

function KycIdentity({ submitting, setSubmitting, setError, onSaved }) {
  const [f, setF] = useState({ passport_number: '', nationality: '', date_of_birth: '', mailing_address: '' })
  const [docType, setDocType] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    // Pre-fill from whatever the platform already holds — including the ID read at signup — so the
    // consultant checks and corrects rather than typing it all again.
    api.get('/onboarding/identity/prefill')
      .then(r => {
        if (!alive) return
        const d = r.data || {}
        setDocType(d.id_document_type || '')
        setF(prev => ({
          passport_number: d.id_number || prev.passport_number,
          nationality: d.nationality || prev.nationality,
          date_of_birth: (d.date_of_birth || '').slice(0, 10) || prev.date_of_birth,
          mailing_address: d.mailing_address || prev.mailing_address,
        }))
      })
      .catch(() => { /* first-time consultant: nothing to prefill, they type it */ })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))

  const submit = async () => {
    for (const [k, label] of [['passport_number', docType || 'ID number'], ['nationality', 'Nationality'], ['mailing_address', 'Mailing address']]) {
      if (!String(f[k] || '').trim()) { setError(`${label} is required.`); return }
    }
    setSubmitting(true); setError(null)
    try {
      await api.post('/onboarding/identity', { ...f, date_of_birth: f.date_of_birth || null })
      onSaved('Identity details submitted for verification.')
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save your identity details.')
    } finally { setSubmitting(false) }
  }

  if (loading) {
    return <p className="text-xs text-gray-400 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading what we already have…</p>
  }

  const field = (key, label, type = 'text', placeholder = '') => (
    <label className="block">
      <span className="text-[11px] font-medium text-gray-600 block mb-1">{label} *</span>
      <input type={type} value={f[key]} placeholder={placeholder} onChange={e => set(key, e.target.value)}
             className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
    </label>
  )

  return (
    <div className="space-y-2">
      {field('passport_number', docType ? `${docType} number` : 'ID number')}
      {field('nationality', 'Nationality')}
      {field('date_of_birth', 'Date of birth', 'date')}
      {field('mailing_address', 'Personal mailing address', 'text', 'Your postal/home address')}
      <button onClick={submit} disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg py-2">
        {submitting ? 'Submitting…' : 'Submit identity'}
      </button>
    </div>
  )
}
